<template>
  <div
    class="media-message"
    :class="[ forceBlack ? 'inbound' : message.direction ]">
    <div
      v-if="message.error"
      class="error">
      <icon name="regular/times-circle"/>
      <span class="error-message">{{ message.error }}</span>
    </div>
    <template v-else>
      <template v-if="['image', 'video'].includes(mediaType)">
        <div
          :style="(mediaType === 'image') ? `background-image: url(${message.media_url ? message.media_url : message.url})` : ''"
          :class="{video: (mediaType === 'video'), 'has-caption': message.body}"
          class="media-image">
          <div class="video-image-preview-constraint">
            <!-- Set without controls just to show the background for the video lightbox. -->
            <video
              v-if="mediaType === 'video'"
              ref="videoImagePlaceholder"
              class="video-image-preview"
              :src="message.media_url ? message.media_url : message.url"
              :type="message.media_type"
              :oncanplay="videoReady()">
              Your browser does not support the video tag.
            </video>
          </div>
          <LightBox
            :type="mediaType"
            :mime="message.media_type"
            :source="message.media_url ? message.media_url : message.url"
            @imageLoaded="messageLoaded()"/>
        </div>
        <div
          v-if="!message.loaded"
          class="loading media-image">
          <Spinner
            :size="24"
            :color="forceBlackLoader || message.direction === 'inbound' ? SBColors.blackPrimary : SBColors.white"
            :thickness="2"/>
        </div>
        <MessageContent
          v-if="message.body || message.text"
          :content="message.body || message.text"
          :convert-tag-to-button="true"
          :detect-urls="true"
          class="caption"/>
      </template>
      <div
        v-else-if="mediaType === 'audio'"
        class="media-audio">
        <!-- TODO cleanup once new player is ready -->
        <!-- <vue-plyr :options="audioPlayerOptions">
          <audio>
            <source
              :src="message.media_url"
              :type="message.media_type">
          </audio>
        </vue-plyr> -->
        <SBAudioPlayer
          :type="message.media_type"
          :url="message.media_url ? message.media_url : message.url" />
      </div>
      <div
        v-else-if="(!message.body || message.body === message.title || message.body === fileName ) && !message.buttons"
        class="media-file basic ">
        <div
          v-if="isToDesktopApp && ['pdf'].includes(mediaType)"
          class="pdf-download show-hand"
          @click="handlePdf(message)">
          <SBIcon
            :icon="SBIconList.DOWNLOAD"
            :size="15"
            :color="message.direction === 'inbound' || forceBlack ? SBColors.blackPrimary : SBColors.white" />
          <div
            class="flex"
            style="max-width: 100%; min-width: 1px;">
            <span class="media-title ellipsis">{{ message.title || fileName | noExtension }}</span>
            <span
              class="no-shrink" >{{ message.title || fileName | extractExtension }}</span>
          </div>
        </div>
        <a
          v-else
          :href="message.media_url ? message.media_url : message.url"
          :target="!isToDesktopApp && ['text', 'pdf'].includes(mediaType) ? '_blank' : ''"
          :download="message.title || fileName">
          <SBIcon
            :icon="SBIconList.DOWNLOAD"
            :size="15"
            :color="message.direction === 'inbound' || forceBlack ? SBColors.blackPrimary : SBColors.white" />
          <div
            class="flex"
            style="max-width: 100%; min-width: 1px;">
            <span class="media-title ellipsis">{{ message.title || fileName | noExtension }}</span>
            <span
              class="no-shrink" >{{ message.title || fileName | extractExtension }}</span>
          </div>
        </a>
      </div>

      <div
        v-else
        class="media-file">
        <div
          v-if="isToDesktopApp && ['pdf'].includes(mediaType)"
          class="pdf-download show-hand"
          @click="handlePdf(message)">
          <SBIcon
            :icon="SBIconList.DOWNLOAD"
            :size="15"
            :color="message.direction === 'inbound' || forceBlack ? SBColors.blackPrimary : SBColors.bluePrimary" />
          <div
            class="flex"
            style="max-width: 100%; min-width: 1px;">
            <span class="media-title ellipsis">{{ message.title || fileName | noExtension }}</span>
            <span
              class="no-shrink" >{{ message.title || fileName | extractExtension }}</span>
          </div>
        </div>
        <a
          v-else
          :href="message.media_url ? message.media_url : message.url"
          :target="!isToDesktopApp && ['text', 'pdf'].includes(mediaType) ? '_blank' : ''"
          :download="message.title || fileName">
          <SBIcon
            :icon="SBIconList.DOWNLOAD"
            :size="15"
            :color="message.direction === 'inbound' || forceBlack ? SBColors.blackPrimary : SBColors.bluePrimary" />
          <div
            class="flex"
            style="max-width: 100%; min-width: 1px;">
            <span class="media-title ellipsis">{{ message.title || fileName | noExtension }}</span>
            <span
              class="no-shrink" >{{ message.title || fileName | extractExtension }}</span>
          </div>
        </a>
        <div
          class="media-caption"
          style="max-width: 100%; min-width: 1px; padding: 11px 10px 8px;">
          <span
            v-if="message.body"
            class="mr-1">{{ message.body }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.media-message {
  overflow: hidden;
  position: relative;


  .video-image-preview-constraint {
    width: 100%;
    height: 100%;
    .video-image-preview {
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  }

  .media-image {
    position: relative;
    background-size: cover;
    background-position: center;
    width: 294px;
    height: 294px;
    display: flex;
    align-items: center;
    overflow: hidden;

    &.loading {
      position: absolute;
      top: 0;
      background: $bluePrimary;
    }

    &.has-caption {
      border-bottom-left-radius: unset;
      border-bottom-right-radius: unset;
    }
  }

  &.inbound .media-image.loading {
    background: $greyChatBubble;
  }

  .caption {
    display: block;
    padding: 7px 10px;
    max-width: 294px;
    word-break: break-word;

    ::v-deep img {
      height: 16px;
      vertical-align: -3px !important;
    }
  }

  .media-audio {
    ::v-deep .audio-player {
      background-color: $greyChatBubble;
      color: $blackPrimary;
      padding: 5px 17px;

      .sb-icon .icon {
        background-color: $blackPrimary !important;
      }

      .progress-wrapper {
        .progress {
          background-color: rgba($blackPrimary, 0.3);

          .progress-bar {
            background-color: $blackPrimary;
          }

          &::after {
            background-color: $blackPrimary;
          }
        }
      }
    }
  }

  &.outbound .media-audio {
    ::v-deep .audio-player {
      background-color: $bluePrimary;
      color: $white;

      .sb-icon .icon {
        background-color: $white !important;
      }

      .progress-wrapper {
        .progress {
          background-color: rgba($white, 0.3);

          .progress-bar {
            background-color: $white;
          }

          &::after {
            background-color: $white;
          }
        }
      }
    }
  }

  .media-file {
    * {
      text-decoration: none !important;
    }

    a, .pdf-download {
      display: flex;
      // justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
      background-color: #e2edfd;
      border-radius: 3px;
      padding: 11px 8px;
      color: $bluePrimary !important;

      ::v-deep .sb-icon {
        margin-right: 8px;
      }

      .media-title {
        white-space: pre-wrap;
        word-wrap: break-word;
        // text-decoration: underline !important;
        max-width: 100%;
        min-width: 1px;
        display: inline-block;
      }

      * {
        text-decoration: none !important;
        color: $bluePrimary !important;
      }
    }

    &.basic {
      a, .pdf-download {
        color: $blackPrimary;
        padding: 4px 14px 4px 9px;
        background-color: unset;
        border-radius: 0;

        * {
          color: $blackPrimary !important;
        }
      }
    }
  }

  &.outbound .media-file.basic {
    a, .pdf-download {
      color: $white !important;
      * {
        color: $white !important;
      }
    }
  }
}
</style>

<script>
// import Axios from 'axios';
import { nativeWindow, webContents } from '@todesktop/client-core';
// eslint-disable-next-line
import VuePlyr from 'vue-plyr';
import { getMediaType, getMediaIcon, getMediaName } from '@/helpers/mediaHelpers';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import Spinner from '@/components/common/Spinner';
import SBAudioPlayer from '@/components/common/AudioPlayer';
// import randomString from '@/helpers/randomString';
import LightBox from '../../../common/LightBox';
import MessageContent from '../../../common/MessageContent';

export default {
  name: 'MediaMessage',
  components: {
    LightBox,
    VuePlyr,
    MessageContent,
    SBIcon,
    Spinner,
    SBAudioPlayer,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    forceBlackLoader: {
      type: Boolean,
      required: false,
      default: false,
    },
    forceBlack: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      audioPlayerOptions: {
        controls: ['play', 'progress', 'current-time', 'mute', 'volume', 'airplay', 'download'],
        tooltips: false,
      },
      SBIconList,
      SBColors,
    };
  },
  computed: {
    fileName() {
      // let derivedName = '';
      // if (this.message.body) {
      //   derivedName = this.message.body;
      // } else {
      //   derivedName = getMediaName(this.message.media_url ? this.message.media_url : this.message.url);
      // }
      // return derivedName;

      return decodeURI(getMediaName(this.message.media_url ? this.message.media_url : this.message.url).replace(/.*fileName=/, ''));
    },
    mediaType() {
      return getMediaType(this.message.media_type ? this.message.media_type : this.message.mime_type);
    },
    mediaIcon() {
      return getMediaIcon(this.mediaType ? this.mediaType : this.message.mime_type);
    },
    isToDesktopApp() {
      return window.todesktop;
    },
  },
  watch: {
    'message.url': function f() {
      this.$set(this.message, 'loaded', false);
    },
  },
  methods: {
    messageLoaded() {
      this.$set(this.message, 'loaded', true);
    },
    videoReady() {
      this.$set(this.message, 'loaded', true);
    },
    async handlePdf(message) {
      const href = message.media_url ? message.media_url : message.url;
      const filename = message.title || this.fileName;

      // Force DOWNLOAD
      // Axios({
      //   url: `${href}?nocache=${randomString()}`, // chrome has a bug when getting image from cache so we need to trick him - https://www.hacksoft.io/blog/handle-images-cors-error-in-chrome
      //   method: 'GET',
      //   responseType: 'blob',
      // })
      //   .then((response) => {
      //     const url = window.URL.createObjectURL(new Blob([response.data]));
      //     const link = document.createElement('a');
      //     link.href = url;
      //     link.setAttribute('download', filename);
      //     document.body.appendChild(link);
      //     link.click();
      //   });

      // Force PREVIEW
      const win = await nativeWindow.create({
        width: 1000,
        height: 800,
        title: 'File preview',
      });
      const contents = await nativeWindow.getWebContents({ ref: win });
      webContents.loadURL({ ref: contents }, href);
    },
  },
};
</script>
